import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { notierungenApi } from '../../api_notierungen'
import { useLaden } from '../../hooks'
import Diagramm from '../../komponenten/Diagramm'
import Meldungen from '../../komponenten/Meldungen'
import { datum, eurT, heuteIso, tagMonat } from '../../format'
import { notierungTabellePfad } from '../../pfade'
import type { NotierungVerlauf, NotierungZeitraum } from '../../typen'

type Reihe = NotierungVerlauf['reihen'][number]

const ZUR_TABELLE = 'In der Tabelle zeigen'

const ARTEN: { art: NotierungZeitraum['art']; titel: string }[] = [
  { art: 'monat', titel: 'Monat' },
  { art: 'quartal', titel: 'Quartal' },
  { art: 'halbjahr', titel: 'Halbjahr' },
  { art: 'jahr', titel: 'Jahr' },
]

/**
 * Der Preisvergleich eines Rohstoffs: je Lieferant eine Linie in eigener Farbe, über die Notierungstage,
 * für einen Lieferzeitraum - Monat, Quartal, Halbjahr oder Jahr. Frau Geigenmüller wünschte sich „eine
 * grafische Darstellung"; hier sieht man, wer für dieselbe Ware am günstigsten ist und ob ein Rückgang
 * Rauschen ist oder ein Trend. Über mehrere Monate ist ein Punkt der Mittelwert der Monate, die der
 * Lieferant an dem Tag notiert hat - wie viele, steht im Tooltip und in der Tabelle, denn ein Mittel aus
 * einem von drei Monaten ist kein Quartalspreis. Mit Transport gerechnet ist Standard: der Preis frei Gera.
 *
 * Ein Klick auf einen Lieferanten - Legende, Linie, Punkt, Spaltenkopf oder Zahl der Tabelle - führt in die
 * Tabelle seiner Gruppe, mit seiner Spalte und den Monaten des Zeitraums hervorgehoben (`tabellenPfad`).
 */
export default function VerlaufSeite() {
  const [suche, setSuche] = useSearchParams()
  const navigate = useNavigate()
  const { daten: rohstoffe, fehler: rohstoffFehler } = useLaden(() => notierungenApi.rohstoffe(), [])
  const aktive = (rohstoffe ?? []).filter((r) => r.aktiv)
  const rohstoffId = Number(suche.get('rohstoff')) || aktive[0]?.id || null
  // `monat` ist die frühere Adresse (Links von „Heute"): ein Monatserster gilt als Monatszeitraum.
  const zeitraum = suche.get('zeitraum') ?? suche.get('monat')?.slice(0, 7) ?? undefined
  const von = suche.get('von') ?? undefined
  const bis = suche.get('bis') ?? undefined
  const inkl = suche.get('inkl') !== 'nein'

  const { daten: verlauf, fehler, laedt } = useLaden(
    () => notierungenApi.verlauf({ rohstoff_id: rohstoffId!, zeitraum, von, bis, inkl_transport: inkl }),
    [rohstoffId, zeitraum, von, bis, inkl],
    { wenn: rohstoffId !== null },
  )

  function setzen(werte: Record<string, string | null>) {
    const neu = new URLSearchParams(suche)
    neu.delete('monat')
    for (const [name, wert] of Object.entries(werte)) {
      if (wert === null || wert === '') neu.delete(name)
      else neu.set(name, wert)
    }
    setSuche(neu, { replace: true })
  }

  const art = verlauf?.zeitraum.art ?? 'monat'
  const zurAuswahl = (verlauf?.zeitraeume ?? []).filter((z) => z.art === art)

  /** Eine andere Art: der erste Zeitraum dieser Art, der den gezeigten Monat enthält - sonst der erste ab heute. */
  function artWaehlen(neueArt: NotierungZeitraum['art']) {
    if (!verlauf || neueArt === art) return
    const kandidaten = verlauf.zeitraeume.filter((z) => z.art === neueArt)
    const passend =
      kandidaten.find((z) => z.monate.includes(verlauf.liefermonat)) ??
      kandidaten.find((z) => z.monate[z.monate.length - 1] >= heuteIso().slice(0, 7) + '-01') ??
      kandidaten[0]
    if (passend) setzen({ zeitraum: passend.schluessel })
  }

  const einheit = inkl ? '€/t inkl. Transport' : '€/t netto'
  const leer = verlauf !== null && (verlauf.x.length === 0 || verlauf.reihen.every((r) => r.werte.every((w) => w === null)))
  const mehrereMonate = (verlauf?.zeitraum.monate.length ?? 1) > 1

  return (
    <section>
      <h2>Verlauf</h2>
      <div className="leiste felder">
        <label className="feld">
          <span className="hinweis">Rohstoff</span>
          <select value={rohstoffId ?? ''} onChange={(e) => setzen({ rohstoff: e.target.value, zeitraum: null })}>
            {aktive.length === 0 && <option value="">keiner angelegt</option>}
            {aktive.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        <div className="feld">
          <span className="hinweis">Lieferzeitraum</span>
          <div className="leiste">
            {ARTEN.map((a) => (
              <button
                key={a.art}
                className={a.art === art ? 'aktiv' : undefined}
                disabled={!verlauf || !verlauf.zeitraeume.some((z) => z.art === a.art)}
                onClick={() => artWaehlen(a.art)}
              >
                {a.titel}
              </button>
            ))}
          </div>
        </div>
        <label className="feld">
          <span className="hinweis">{ARTEN.find((a) => a.art === art)?.titel}</span>
          <select value={verlauf?.zeitraum.schluessel ?? ''} onChange={(e) => setzen({ zeitraum: e.target.value })}>
            {zurAuswahl.length === 0 && <option value="">{verlauf?.zeitraum.text ?? '…'}</option>}
            {zurAuswahl.map((z) => (
              <option key={z.schluessel} value={z.schluessel}>
                {z.text}
              </option>
            ))}
          </select>
        </label>
        <label className="feld">
          <span className="hinweis">notiert ab</span>
          <input type="date" value={von ?? ''} onChange={(e) => setzen({ von: e.target.value })} />
        </label>
        <label className="feld">
          <span className="hinweis">bis (leer = heute)</span>
          <input type="date" value={bis ?? ''} onChange={(e) => setzen({ bis: e.target.value })} />
        </label>
        <label className="haekchen" title="Mit dem Transportzuschlag der Spalte - der Preis frei Gera">
          <input type="checkbox" checked={inkl} onChange={(e) => setzen({ inkl: e.target.checked ? null : 'nein' })} /> inkl. Transport
        </label>
      </div>
      <Meldungen fehler={rohstoffFehler ?? fehler} />
      {laedt && !verlauf && <p className="hinweis">Wird geladen.</p>}
      {leer && (
        <p className="leer">
          Für {verlauf.rohstoff}, {verlauf.zeitraum.text}, liegen keine Preise vor
          {von || bis ? ' - einen früheren Beginn oder ein späteres Ende wählen' : ''}.
        </p>
      )}
      {verlauf && !leer && (
        <>
          <Diagramm
            typ="linie"
            titel={`${verlauf.rohstoff}, Lieferung ${verlauf.zeitraum.text} - ${einheit}${mehrereMonate ? ', Mittel der notierten Monate' : ''}`}
            x={verlauf.x.map(tagMonat)}
            reihen={verlauf.reihen.map((r) => ({ name: reihenName(r), werte: r.werte, hinweise: mehrereMonate ? r.monate.map((n) => monateText(n, verlauf)) : undefined }))}
            einheit="€/t"
            abNull={false}
            // Zahlen an den Punkten nur, solange sie sich nicht überlagern - sonst Tooltip und Tabelle darunter.
            werteZeigen={verlauf.x.length <= 8 && verlauf.reihen.length <= 3}
            gross
            onReihe={(r, punkt) => navigate(tabellenPfad(verlauf, verlauf.reihen[r], punkt))}
            klickText={ZUR_TABELLE}
          />
          <p className="hinweis">Ein Klick auf einen Lieferanten - in der Legende, auf Linie oder Punkt, in der Tabelle - zeigt ihn in der Tabelle.</p>
          <Tabelle verlauf={verlauf} mehrereMonate={mehrereMonate} />
        </>
      )}
    </section>
  )
}

/** Die Spalte heißt oft wie der Lieferant („Olenex SG“) - die Qualität nur dazu, wenn sie nicht schon drinsteht. */
function reihenName(r: NotierungVerlauf['reihen'][number]): string {
  return r.qualitaet && !r.name.includes(r.qualitaet) ? `${r.name} ${r.qualitaet}` : r.name
}

/**
 * Wohin ein Klick auf einen Lieferanten führt: das Blatt seiner Gruppe am angeklickten Tag - ohne Punkt
 * am letzten Tag mit Preis -, seine Spalte und die Monate des gewählten Zeitraums hervorgehoben.
 */
function tabellenPfad(verlauf: NotierungVerlauf, reihe: Reihe, punkt?: number): string {
  let tag = punkt ?? -1
  if (punkt === undefined) for (let i = reihe.werte.length - 1; i >= 0 && tag < 0; i--) if (reihe.werte[i] !== null) tag = i
  return notierungTabellePfad(verlauf.gruppe, verlauf.x[tag], { spalte: reihe.quelle_id, monate: verlauf.zeitraum.monate })
}

function monateText(anzahl: number | null, verlauf: NotierungVerlauf): string | null {
  if (anzahl === null) return null
  return `Mittel aus ${anzahl} von ${verlauf.zeitraum.monate.length} Monaten`
}

/** Dieselben Zahlen zum Nachsehen und Kopieren, neuester Tag oben. Über mehrere Monate mit der Zahl der Monate. */
function Tabelle({ verlauf, mehrereMonate }: { verlauf: NotierungVerlauf; mehrereMonate: boolean }) {
  const anzahl = verlauf.zeitraum.monate.length
  return (
    <div className="tabellenrahmen nz-verlauf-tabelle">
      <table>
        <thead>
          <tr>
            <th>Notiert am</th>
            {verlauf.reihen.map((r) => (
              <th key={r.quelle_id} className="zahl">
                <Link to={tabellenPfad(verlauf, r)} title={ZUR_TABELLE}>
                  {reihenName(r)}
                </Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {verlauf.x
            .map((tag, i) => ({ tag, i }))
            .reverse()
            .map(({ tag, i }) => (
              <tr key={tag}>
                <td>{datum(tag)}</td>
                {verlauf.reihen.map((r) => {
                  const n = r.monate[i]
                  const titel = [mehrereMonate && monateText(n, verlauf), `${ZUR_TABELLE} (Stand ${datum(tag)})`].filter(Boolean).join(' - ')
                  return (
                    <td key={r.quelle_id} className="zahl" title={r.werte[i] !== null ? titel : undefined}>
                      {r.werte[i] !== null && <Link to={tabellenPfad(verlauf, r, i)}>{eurT(r.werte[i])}</Link>}
                      {mehrereMonate && n !== null && n < anzahl && <div className="hinweis">aus {n} von {anzahl} Mon.</div>}
                    </td>
                  )
                })}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
}
